import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Loader2, ShieldCheck, Trash2, Smartphone, MonitorSmartphone } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { registerPasskey, passkeyPlatformAvailable } from "../lib/passkey";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("ar", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
};

export const PasskeySettingCard = () => {
  const [supported, setSupported] = useState(true);
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setDevices((await api.webauthnDevices()).items || []); }
    catch { setDevices([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { passkeyPlatformAvailable().then(setSupported); load(); }, []);

  const enable = async () => {
    setBusy(true);
    try { await registerPasskey(); toast.success("تم تفعيل الدخول بالبصمة على هذا الجهاز"); await load(); }
    catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "تم إلغاء العملية" : (e?.response?.data?.detail || "تعذّر تفعيل البصمة"));
    } finally { setBusy(false); }
  };
  const removeDevice = async (id) => {
    setRemoving(id);
    try { await api.webauthnRemoveDevice(id); toast.success("تم إزالة الجهاز"); await load(); }
    catch { toast.error("تعذّر إزالة الجهاز"); }
    finally { setRemoving(null); }
  };

  if (!supported) return null;
  return (
    <div data-testid="passkey-card" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
          <Fingerprint className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-slate-800 text-sm">الدخول بالبصمة / التعرّف على الوجه</p>
          <p className="text-xs text-slate-500 mt-0.5">فعّلها على هذا الجهاز لتسجيل الدخول بسرعة دون كلمة المرور. تعمل على هذا الجهاز والمتصفح فقط.</p>

          {loading ? (
            <Loader2 className="mt-3 h-4 w-4 animate-spin text-slate-400" />
          ) : (
            <>
              {devices.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="passkey-devices">
                  {devices.map((d) => (
                    <div key={d.device_id} data-testid={`passkey-device-${d.device_id}`}
                      className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2">
                      <div className="h-8 w-8 shrink-0 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500">
                        {/iPhone|Android/i.test(d.device_name || "") ? <Smartphone className="h-4 w-4" /> : <MonitorSmartphone className="h-4 w-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1.5">
                          {d.device_name}
                          {d.this_device && <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5">هذا الجهاز</span>}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate">
                          فُعّل {fmtDate(d.created_at)}{d.last_used_at ? ` · آخر استخدام ${fmtDate(d.last_used_at)}` : ""}
                        </p>
                      </div>
                      <button data-testid={`passkey-device-remove-${d.device_id}`} disabled={removing === d.device_id}
                        onClick={() => removeDevice(d.device_id)}
                        className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-rose-600 transition disabled:opacity-50">
                        {removing === d.device_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {devices.some((d) => d.this_device) ? (
                <span data-testid="passkey-enabled-badge" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1">
                  <ShieldCheck className="h-3.5 w-3.5" /> مُفعّل على هذا الجهاز
                </span>
              ) : (
                <Button size="sm" data-testid="passkey-enable-btn" disabled={busy} onClick={enable}
                  className="mt-3 h-9 gap-1.5 bg-blue-600 hover:bg-blue-700">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} تفعيل الدخول بالبصمة على هذا الجهاز
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
